import { AppStore } from './state/app-store';
import { VehicleMovements } from './../models/vehicle-movements.model';
import { Injectable } from '@angular/core';

import { FilterData } from '../models/filter-input.model';
import { Position } from '../models/api/vehicle-position.model';
import { VehiclesService } from './vehicles.service';
import {
  VehicleStatuses,
  InternalVehicleStatus
} from '../models/vehicle-statuses.model';
import { UserVehicleGroup } from '../models/vehicle-types.model';
import { Pharmacy } from './pharmacy.service';
import { AlarmService } from './alarm.service';
import { alarmStatus } from '../config/app.config';

@Injectable({
  providedIn: 'root'
})
export class FilterVehiclesService {
  private readonly alarmStatus = alarmStatus;
  private readonly filterKeyExclude: string[] = ['uuid'];

  constructor(
    public vehiclesService: VehiclesService,
    private appStore: AppStore,
    private alarmService: AlarmService
  ) {
  }

  public filterPositions(src: Position[], filterData: FilterData): Position[] {
    let results: Position[] = src;
    if (filterData.criteria.byText && filterData.text && filterData.pattern) {
      results = this.filterPositionsByText(results, filterData);
    }
    if (filterData.criteria.byGroup && filterData.group) {
      results = this.filterPositionsByGroup(results, filterData);
    }

    if (filterData.criteria.byStatus && filterData.status) {
      results = this.filterPositionsByStatus(results, filterData);
    }

    return this.getUnique(results);
  }

  public filterPharmacyList(
    src: Pharmacy[],
    filterData: FilterData
  ): Pharmacy[] {
    let results: Pharmacy[] = src;

    if (filterData.criteria.byText && filterData.text && filterData.pattern) {
      results = this.filterPharmacyListByText(results, filterData);
    }
    if (filterData.criteria.byGroup && filterData.group) {
      results = this.filterPharmacyListByGroup(results, filterData);
    }

    if (filterData.criteria.byStatus && filterData.status) {
      results = this.filterPharmacyListByStatus(results, filterData, 'status');
    }

    if (filterData.criteria.byStatus && filterData.status) {
      results = this.filterPharmacyListByAlarm(results, filterData, 'alarms');
    }

    return results;
    // return this.getUniquePharmacy(results);
  }

  private filterPharmacyListByStatus(
    pharmacyList: Pharmacy[],
    filterData: FilterData,
    type: string = 'status'
  ): Pharmacy[] {
    if (!pharmacyList) {
      return [];
    }

    let statusList = [];

    if (type === 'alarms') {
      statusList = filterData.status.filter((status) =>
        this.alarmStatus.includes(status)
      );
    } else {
      statusList = filterData.status.filter(
        (status) => !this.alarmStatus.includes(status)
      );
    }

    if (statusList && statusList.length > 0) {
      return pharmacyList.filter((pharmacy: Pharmacy) => {
        if (pharmacy.position && pharmacy.position.length > 0) {
          return pharmacy.position.some((position: Position) => {
            return (
              statusList.includes(this.checkVehicleStatus(position)) ||
              statusList.length === 0 ||
              statusList === null
            );
          });
        }
        return false;
      });
    }

    return pharmacyList;
  }

  private filterPharmacyListByAlarm(
    pharmacyList: Pharmacy[],
    filterData: FilterData,
    type: string = 'status'
  ): Pharmacy[] {
    if (!pharmacyList) {
      return [];
    }

    let statusList = [];

    if (type === 'alarms') {
      statusList = filterData.status.filter((status) =>
        this.alarmStatus.includes(status)
      );
    } else {
      statusList = filterData.status.filter(
        (status) => !this.alarmStatus.includes(status)
      );
    }

    if (statusList && statusList.length > 0) {
      return pharmacyList.filter((pharmacy: Pharmacy) => {
        if (pharmacy.position && pharmacy.position.length > 0) {
          return pharmacy.position.some((position: Position) => {
            return statusList.includes(this.checkVehicleAlarm(position));
          });
        }
        return false;
      });
    }

    return pharmacyList;
  }

  private filterPharmacyListByText(
    pharmacyList: Pharmacy[],
    filterData: FilterData
  ): Pharmacy[] {
    if (!pharmacyList) {
      return pharmacyList;
    }
    return pharmacyList.filter((pharmacy: Pharmacy) => {
      if (
        typeof pharmacy.name === 'string' &&
        filterData.pattern.test(pharmacy.name)
      ) {
        return true;
      }

      for (const key in pharmacy) {
        if (this.filterKeyExclude.includes(key)) {
          continue;
        }

        if (typeof pharmacy[key] === 'string') {
          if (filterData.pattern.test(pharmacy[key])) {
            return true;
          }
        } else if (key === 'position' && Array.isArray(pharmacy[key])) {
          return pharmacy[key].some((position: Position) => {
            if (
              filterData.pattern.test(position.name) ||
              filterData.pattern.test(position.vehicle)
            ) {
              return true;
            }
            return position.extcolumns.ext.some(
              (item: { name: string; value: string }) =>
                filterData.pattern.test(item.name) ||
                filterData.pattern.test(item.value)
            );
          });
        }
      }

      return false;
    });
  }

  private filterPositionsByText(
    src: Position[],
    filterData: FilterData
  ): Position[] {
    return src.filter((position: Position) => {
      for (const key in position) {
        if (typeof position[key] === 'string') {
          if (filterData.pattern.test(position[key])) {
            return true;
          }
        } else if (key === 'extcolumns') {
          return !!position[key].ext.find(
            (item: { name: string; value: string }) =>
              filterData.pattern.test(item.name) ||
              filterData.pattern.test(item.value)
          );
        }
      }
      return false;
    });
  }

  private filterPharmacyListByGroup(
    pharmacyList: Pharmacy[],
    filterData: FilterData
  ): Pharmacy[] {
    if (Array.isArray(filterData.group) && filterData.group.length > 0) {
      let vehiclesIdsInGroup = [];

      vehiclesIdsInGroup = filterData.group.map((filter) => {
        return filter.vehiclesIds;
      });

      return pharmacyList.filter((pharmacy) =>
        pharmacy.groups.some((group) =>
          filterData.group.some((obj) => obj.id === group)
        )
      );
    }

    return pharmacyList;
  }

  private filterPositionsByGroup(
    src: Position[],
    filterData: FilterData
  ): Position[] {
    if (Array.isArray(filterData.group) && filterData.group.length > 0) {
      let vehiclesIdsInGroup = [];

      vehiclesIdsInGroup = filterData.group.map((filter) => {
        return filter.vehiclesIds;
      });

      return src.filter((positions) =>
        vehiclesIdsInGroup.includes(parseInt(positions.vehicle))
      );
    }

    return src;
  }

  private filterPositionsByStatus(
    src: Position[],
    filterData: FilterData
  ): Position[] {
    return src.filter((position: Position) => {
      return (filterData.status.includes(this.checkVehicleStatus(position)) ||
        filterData.status.includes(this.checkVehicleMovement(position)) ||
        filterData.status.length === 0 || false);
    });
  }

  checkVehicleMovement(position: Position): VehicleMovements {
    if (position.speed === '255') {
      return VehicleMovements.OFF;
    } else if (position.speed) {
      const internalStatus = this.checkVehicleStatus(position);
      if (
        internalStatus === InternalVehicleStatus.OK ||
        internalStatus === InternalVehicleStatus.DELAYED
        // ||
        // internalStatus === InternalVehicleStatus.POWER_WARNING
      ) {
        return VehicleMovements.ON;
      }
    }
  }

  private checkVehicleStatus(position: Position) {
    let status;

    if (position.statusicons && position.statusicons.icon) {
      status = position.statusicons.icon;
      status = Array.isArray(status) ? status : [status];
    } else {
      return InternalVehicleStatus.NO_DATA;
    }

    if (
      status.some((stat) => stat.token === VehicleStatuses.POWER_WARNING) ||
      status.some((stat) => stat.token === VehicleStatuses.POWER_CRITICAL)
    ) {
      return InternalVehicleStatus.POWER_CRITICAL;
    } else if (this.checkDisconnectionFromRS485(position)) {
      return InternalVehicleStatus.DISCONNECTED;
    } else if (status.some((stat) => stat.token === VehicleStatuses.TRANSMISSION_CRITICAL)) {
      return InternalVehicleStatus.TRANSMISSION_CRITICAL;
    } else {
      const min = 60 * 1000;
      const hour = min * 60;
      const isVehicleOn = position.speed !== '255';
      let firstThreshold;
      let secondThreshold;
      if (isVehicleOn) {
        firstThreshold = 5 * min;
        secondThreshold = 4 * hour + 30 * min;
      } else {
        firstThreshold = 40 * min;
        secondThreshold = 24 * hour + 30 * min;
      }
      const lastUpdateTime = position.time
        ? parseInt(position.time) * 1000
        : void 0;
      let lastUpdateDuration = new Date().getTime() - lastUpdateTime;
      lastUpdateDuration = lastUpdateDuration > 0 ? lastUpdateDuration : 0;
      if (lastUpdateDuration <= firstThreshold) {
        return InternalVehicleStatus.OK;
      } else if (lastUpdateDuration <= secondThreshold) {
        return InternalVehicleStatus.DELAYED;
      } else {
        return InternalVehicleStatus.UNAVAILABLE;
      }
    }
  }

  private checkVehicleAlarm(position: Position) {
    if (this.hasAlarmByType(position, InternalVehicleStatus.TEMPERATURE)) {
      return InternalVehicleStatus.TEMPERATURE;
    } else if (this.hasAlarmByType(position, InternalVehicleStatus.DROPLET)) {
      return InternalVehicleStatus.DROPLET;
    } else if (this.hasAlarmByType(position, InternalVehicleStatus.STATION)) {
      return InternalVehicleStatus.STATION;
    }
  }

  private hasAlarmByType(
    position: Position,
    alarmType: InternalVehicleStatus
  ): boolean {
    const alarmsList = this.alarmService.alarmsList;

    if (!alarmsList || !Array.isArray(alarmsList)) {
      return false;
    }

    return !!alarmsList.find((alarm) => {
      return (
        alarm.vehicleId === +position.vehicle &&
        alarm.alarmsType.includes(alarmType)
      );
    });
  }

  private checkDisconnectionFromRS485(data) {
    const vehicle = this.appStore.getState().vehicles.find(
      (value) => value.id === Number(data.vehicle)
    );
    if (!vehicle || !Array.isArray(vehicle.sensors)) {
      return false;
    }
    const sensors = vehicle.sensors;
    if (
      !data.extcolumns ||
      !data.extcolumns.ext ||
      !Array.isArray(data.extcolumns.ext)
    ) {
      return false;
    }
    let fieldsToCheck = ['EXT_DST1', 'EXT_DST2', 'EXT_DST3', 'EXT_DST4'];
    fieldsToCheck = fieldsToCheck.filter((field) => {
      const lastDigit = parseInt(field.substring(field.length - 1), 10);
      if (isNaN(lastDigit)) return false;

      const matchedSensors = sensors.filter((s) => s.slot === lastDigit);

      const validSensor = matchedSensors.find(s => s.mac && s.mac.trim() !== '' && s.mac !== 0);
      return !!validSensor;
    });

    const disconnectionValue = 'Odłączenie od magistrali RS-485';

    return data.extcolumns.ext.some(
      (item) =>
        fieldsToCheck.includes(item.name) && item.value === disconnectionValue
    );
  }

  private getUnique(arr: any[], property = 'vehicle'): any[] {
    if (arr) {
      const unique = arr
        .map((e) => e[property])
        // store the keys of the unique objects
        .map((e, i, final) => final.indexOf(e) === i && i)
        // eliminate the dead keys & store unique objects
        .filter((e) => arr[e])
        .map((e) => arr[e]);
      if (
        this.appStore.getState().selectedVehicle &&
        !unique
          .map((vehicle) => vehicle.vehicle)
          .includes(this.appStore.getState().selectedVehicle.vehicle)
      ) {
        this.appStore.unselectVehicle();
      }

      return unique;
    }
  }
}
